import {
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Rating,
  Select,
  TextField,
  Typography
} from '@mui/material';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { useLocation, useNavigate, useParams } from 'react-router';
import CircleIcon from '@mui/icons-material/Circle';
import './Resource.css';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import AllocateModal from './AllocateModal';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import {
  getSingleResourceAllocation,
  resourceAllocationUpdate,
  resourceDeAllocationMultiple
} from 'src/services/projectService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { AppActions } from 'src/store/AppReducer';
import { useDispatch } from 'react-redux';

const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const ResourceSinglePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  let query = useQuery();
  const [resource, setResource] = useState<any>({});
  const [updateData, setUpdateData] = useState<any>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [deallocateIDS, setDeallocateIDS] = useState<any>([]);
  const [visibility, setVisibility] = useState('');

  useEffect(() => {
    setVisibility(query.get('view'));
  }, [query]);

  useEffect(() => {
    if (visibility === 'false') {
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
  }, [visibility]);

  useEffect(() => {
    setUpdateData(resource?.allocations);
  }, [resource]);

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const getSingleResourceData = () => {
    dispatch(AppActions.showLoader());

    getSingleResourceAllocation(employeeId).then((res) => {
      setResource(res.data || {});
      dispatch(AppActions.hideLoader());
    });
  };
  useEffect(() => {
    getSingleResourceData();
  }, [employeeId]);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const onUpdate = () => {
    const updatableData = updateData.map((up) => {
      return {
        allocationId: up.id,
        billableType: up.type,
        startDate: up.startDate,
        endDate: up.endDate,
        allocation: up.allocation
      };
    });

    dispatch(AppActions.showLoader());
    if (deallocateIDS.length > 0) {
      resourceDeAllocationMultiple(deallocateIDS)
        .then(() => {
          setSnackBarMessage('You have updated allocations successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          getSingleResourceData();
          dispatch(AppActions.hideLoader());
        })
        .catch((e) => {
          setSnackBarMessage('Unable to update allocations.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          dispatch(AppActions.hideLoader());
        });
    }

    resourceAllocationUpdate(updatableData)
      .then(() => {
        getSingleResourceData();
        setSnackBarMessage('You have updated allocations successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        setIsEdit(false);
        dispatch(AppActions.hideLoader());
      })
      .catch((e) => {
        setSnackBarMessage('Unable to update allocations.');
        setSnackbarSeverity('error');
        dispatch(AppActions.hideLoader());
        setSnackBarOpen(true);
      });
  };

  const colors = [
    '#ebc44c',
    '#db4c64',
    '#a4d4cc',
    '#eeebe5',
    '#61dc87',
    '#250330',
    '#ed4ac9',
    '#bf754a',
    '#bf2128',
    '#fee1a4',
    '#0000ff',
    '#38761d'
  ];

  return (
    <Fragment>
      <PageTitleWrapper>
        <Grid container sx={{ p: 0 }}>
          <Grid item>
            <Typography
              onClick={() => {
                navigate('/resource-management');
              }}
              style={{ cursor: 'pointer' }}
              variant="h4"
              component="h4"
            >
              Resource Management /
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              style={{ fontWeight: 'normal' }}
              variant="h4"
              component="h4"
            >
              {resource.name}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: 'auto' }}>
            <Button
              sx={{
                mr: 2,
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => toggle()}
              style={{ height: '1.75rem' }}
            >
              Allocate
            </Button>{' '}
          </Grid>
        </Grid>
      </PageTitleWrapper>
      <Container maxWidth="lg">
        {resource?.allocations?.length > 0 && (
          <Grid container sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Card style={{ padding: '1rem' }}>
              <Grid container display="flex" flexDirection="column">
                <Grid item display="flex" justifyContent="center">
                  <Grid style={{ width: '50%' }}>
                    {resource?.allocations?.length > 0 && (
                      <PieChart
                        data={resource?.allocations?.map((allo, index) => ({
                          title: allo.projectName,
                          value: allo.allocation,
                          color: `${colors[index]}`
                        }))}
                        lineWidth={40}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid
                  container
                  xs={12}
                  style={{ marginTop: '1rem' }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {resource?.allocations?.map((allo, index) => (
                    <Grid
                      xs={5}
                      style={{
                        width: '100%',
                        marginTop: '0.5rem'
                      }}
                      alignItems="center"
                      display="flex"
                    >
                      <Grid item xs={3}>
                        <CircleIcon
                          style={{
                            fontSize: '11px',
                            textAlign: `${index % 2 !== 0 ? 'right' : 'left'}`,
                            color: `${
                              allo.allocation >= 50
                                ? '#A1D0C9'
                                : allo.allocation >= 25
                                ? '#DA4C61'
                                : '#3C3D51'
                            }`
                          }}
                        />
                      </Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="body1"
                          style={{
                            fontSize: '11px',
                            textAlign: `${index % 2 !== 0 ? 'right' : 'center'}`
                          }}
                          color="text.primary"
                        >
                          {`${allo.projectName} - ${allo.allocation}%`}
                        </Typography>{' '}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid
                  container
                  xs={12}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  style={{ marginBottom: '4rem' }}
                >
                  <Grid
                    display="flex"
                    style={{
                      marginTop: '3rem',
                      alignItems: 'center',
                      width: '100%'
                    }}
                    xs={12}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="body1"
                      style={{
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}
                      color="text.primary"
                      gutterBottom
                      noWrap
                    >
                      Skills
                    </Typography>

                    <Typography
                      variant="body1"
                      style={{
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}
                      color="text.primary"
                      gutterBottom
                      noWrap
                    >
                      {resource?.skills?.length}
                    </Typography>
                  </Grid>
                  {resource?.skills?.length > 0 &&
                    resource?.skills?.map((skill, index) => (
                      <Grid
                        display="flex"
                        style={{
                          marginTop: '1.5rem',
                          alignItems: 'center',
                          width: '100%'
                        }}
                        item
                        xs={12}
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="body1"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {skill?.name}
                        </Typography>
                        <Rating
                          readOnly
                          name="customized-color"
                          defaultValue={skill?.level}
                          precision={1}
                          style={{ color: `${colors[index]}` }}
                          icon={
                            <CircleIcon
                              style={{ fontSize: '14px', marginLeft: '0.5rem' }}
                            />
                          }
                          emptyIcon={
                            <CircleIcon
                              style={{ fontSize: '14px', marginLeft: '0.5rem' }}
                            />
                          }
                        />
                      </Grid>
                    ))}
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={8} style={{ paddingLeft: '1rem' }}>
            <Card style={{ padding: '1rem' }}>
              {updateData?.length > 0 &&
                resource &&
                resource?.allocations?.length > 0 &&
                resource?.allocations?.map((allo, index) => (
                  <Grid style={{ marginBottom: '2rem' }}>
                    <Typography variant="h4" style={{ fontSize: '14px' }}>
                      {allo.projectName}
                    </Typography>
                    <Grid container spacing={2} sx={{ pt: 2, mb: '0.25rem' }}>
                      <Grid item xs={6}>
                        <Typography
                          style={{ fontSize: '11px' }}
                          color="GrayText"
                        >
                          Project Role
                        </Typography>
                        <FormControl fullWidth>
                          <TextField
                            inputProps={{ style: { fontSize: '11px' } }}
                            disabled
                            size="small"
                            placeholder="Project Role"
                            variant="outlined"
                            value={allo?.role || '-'}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          style={{ fontSize: '11px' }}
                          color="GrayText"
                        >
                          Billable Type
                        </Typography>
                        <FormControl fullWidth>
                          {!isEdit ? (
                            <TextField
                              inputProps={{ style: { fontSize: '11px' } }}
                              disabled={!isEdit}
                              size="small"
                              placeholder="Billing Type"
                              variant="outlined"
                              value={allo.type}
                            />
                          ) : (
                            <Select
                              size="small"
                              displayEmpty
                              defaultValue={''}
                              inputProps={{ 'aria-label': 'Without label' }}
                              style={{
                                fontSize: '11px'
                              }}
                              value={updateData[index]?.type}
                              onChange={(e) => {
                                const newArr = [...updateData];
                                newArr[index].type = e.target.value;
                                setUpdateData(newArr);
                              }}
                            >
                              <MenuItem
                                style={{ display: 'none' }}
                                selected
                                disabled
                                value=""
                              >
                                Billable Type
                              </MenuItem>
                              <MenuItem value="Billable">Billable</MenuItem>
                              <MenuItem value="Non-Billable">
                                Non-Billable
                              </MenuItem>
                              <MenuItem value="Shadow">Shadow</MenuItem>
                              <MenuItem value="Support">Support</MenuItem>
                              <MenuItem value="T&M">T&M</MenuItem>
                            </Select>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          style={{ fontSize: '11px' }}
                          color="GrayText"
                        >
                          Start Date
                        </Typography>
                        <FormControl fullWidth>
                          {!isEdit ? (
                            <TextField
                              inputProps={{ style: { fontSize: '11px' } }}
                              disabled={!isEdit}
                              size="small"
                              variant="outlined"
                              value={moment(allo.startDate).format(
                                'DD/MM/YYYY'
                              )}
                            />
                          ) : (
                            <TextField
                              inputProps={{ style: { fontSize: '11px' } }}
                              size="small"
                              variant="outlined"
                              type="date"
                              value={moment(
                                updateData[index]?.startDate
                              ).format('YYYY-MM-DD')}
                              onChange={(e) => {
                                const newArr = [...updateData];
                                newArr[index].startDate = e.target.value;
                                setUpdateData(newArr);
                              }}
                            />
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          style={{ fontSize: '11px' }}
                          color="GrayText"
                        >
                          End Date
                        </Typography>
                        <FormControl fullWidth>
                          {!isEdit ? (
                            <TextField
                              inputProps={{ style: { fontSize: '11px' } }}
                              disabled={!isEdit}
                              size="small"
                              variant="outlined"
                              value={moment(allo.endDate).format('DD/MM/YYYY')}
                            />
                          ) : (
                            <TextField
                              inputProps={{ style: { fontSize: '11px' } }}
                              size="small"
                              placeholder="DD/MM/YYYY"
                              type="date"
                              variant="outlined"
                              value={moment(updateData[index]?.endDate).format(
                                'YYYY-MM-DD'
                              )}
                              onChange={(e) => {
                                const newArr = [...updateData];
                                newArr[index].endDate = e.target.value;
                                setUpdateData(newArr);
                              }}
                            />
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          style={{ fontSize: '11px' }}
                          color="GrayText"
                        >
                          Allocation (%)
                        </Typography>
                        <FormControl fullWidth>
                          {!isEdit ? (
                            <TextField
                              inputProps={{ style: { fontSize: '11px' } }}
                              disabled={!isEdit}
                              size="small"
                              placeholder="Project Name"
                              variant="outlined"
                              value={allo.allocation}
                            />
                          ) : (
                            <TextField
                              inputProps={{ style: { fontSize: '11px' } }}
                              size="small"
                              placeholder="Allocation"
                              variant="outlined"
                              type="number"
                              value={updateData[index]?.allocation || ''}
                              onChange={(e) => {
                                const newArr = [...updateData];
                                newArr[index].allocation = Number(
                                  e.target.value
                                );
                                setUpdateData(newArr);
                              }}
                            />
                          )}
                        </FormControl>
                      </Grid>
                      {isEdit && (
                        <Grid item display="flex" alignItems="center">
                          <Checkbox
                            checked={deallocateIDS.includes(allo?.id)}
                            color="primary"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDeallocateIDS([...deallocateIDS, allo?.id]);
                              } else {
                                setDeallocateIDS(
                                  deallocateIDS.filter(
                                    (fil) => fil !== allo?.id
                                  )
                                );
                              }
                            }}
                          />
                          <Typography
                            style={{ fontSize: '11px' }}
                            color="GrayText"
                          >
                            Deallocate
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
              {isEdit && (
                <Grid
                  container
                  style={{ justifyContent: 'flex-end', display: 'flex' }}
                >
                  <Button
                    type="button"
                    onClick={() => navigate('/resource-management')}
                    style={{ fontSize: '12px', fontWeight: 'normal' }}
                    sx={{ marginRight: 2, mb: 3, mt: 2 }}
                    variant="outlined"
                    id="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={resource?.allocations?.length === 0}
                    type="button"
                    style={{ fontSize: '12px', fontWeight: 'normal' }}
                    sx={{ marginRight: 2, mb: 3, mt: 2 }}
                    variant="contained"
                    id="update-button"
                    onClick={() => onUpdate()}
                  >
                    Update
                  </Button>
                </Grid>
              )}
            </Card>
          </Grid>
        </Grid>
        ) }
        {resource?.allocations?.length === 0 && (
                      <Typography
                        style={{
                          fontSize: '15px',
                          fontWeight: 'bold',
                          margin: '1rem',
                          textAlign: 'center'
                        }}
                        variant="body2"
                      >
                        No Records Found
                      </Typography>
                    )}
      </Container>
      <AllocateModal
        toggle={toggle}
        isOpen={isOpen}
        getData={getSingleResourceData}
        setSnackBarOpen={setSnackBarOpen}
        setSnackBarMessage={setSnackBarMessage}
        setSnackbarSeverity={setSnackbarSeverity}
        singlePage={{
          name: resource?.name,
          employeeId: resource?.employeeId
        }}
      />
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </Fragment>
  );
};

export default ResourceSinglePage;
